import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  EmojiEvents,
  Star,
  VerifiedUser,
  Refresh,
  Share,
  Download,
  CheckCircle,
  TrendingUp,
  Home
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useScore } from '../context/ScoreContext';
import { useUser } from '../context/UserContext';
import StarRating from '../components/Gamification/StarRating';
import AchievementBadge, { ACHIEVEMENTS } from '../components/Gamification/AchievementBadge';

const ResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const { state: scoreState } = useScore();
  const { state: userState } = useUser();

  const improvements = [
    'Obtain an EPC rating of E or better',
    'Schedule annual Gas Safety Certificate inspection',
    'Arrange Electrical Installation Condition Report (EICR)',
    'Install working smoke alarms on every floor',
    'Apply for HMO license if required',
    'Register tenant deposits in approved protection scheme'
  ];

  const nextLevelRequirements = [
    'Complete tenancy compliance documentation',
    'Ensure all safety certificates are current',
    'Add accessibility features assessment',
    'Include environmental compliance check'
  ];

  // Mock achievement unlocking based on score
  const unlockedAchievements = ACHIEVEMENTS.map(achievement => ({
    ...achievement,
    unlocked:
      (achievement.id === 'first_steps' && scoreState.answers.length > 0) ||
      (achievement.id === 'bronze_level' && scoreState.starRating >= 2) ||
      (achievement.id === 'silver_level' && scoreState.starRating >= 3) ||
      (achievement.id === 'gold_level' && scoreState.starRating >= 4) ||
      (achievement.id === 'platinum_level' && scoreState.starRating >= 5)
  }));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Critical Alert for Zero Stars */}
      {scoreState.starRating === 0 && (
        <motion.div variants={itemVariants}>
          <Card sx={{
            mb: 4,
            background: 'linear-gradient(135deg, #ff1744 0%, #d32f2f 100%)',
            color: 'white',
            border: '3px solid #ff5722',
            boxShadow: '0 0 20px rgba(255, 23, 68, 0.5)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  ⚠️ URGENT COMPLIANCE ALERT ⚠️
                </Typography>
                <Typography variant="h5" sx={{ mb: 3 }}>
                  SERIOUS TENANCY COMPLIANCE ISSUES
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, fontSize: '1.2rem' }}>
                  Critical tenancy compliance issues have been identified that require <strong>IMMEDIATE ATTENTION</strong>.
                  This property may not be legally compliant for rental occupation until these issues are resolved.
                </Typography>
                <Box sx={{
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  padding: 2,
                  borderRadius: 2,
                  mb: 3
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    IMMEDIATE ACTIONS REQUIRED:
                  </Typography>
                  <Typography variant="body2">
                    • Contact qualified professionals immediately<br />
                    • Do not occupy property until repairs are completed<br />
                    • Consider temporary alternative accommodation<br />
                    • Notify relevant authorities if required
                  </Typography>
                </Box>
                <StarRating rating={0} size="large" />
              </Box>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Hero Results Section */}
      <motion.div variants={itemVariants}>
        <Card sx={{
          mb: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  mx: 'auto',
                  mb: 3,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <EmojiEvents sx={{ fontSize: 50 }} />
              </Avatar>

              <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
                Congratulations!
              </Typography>

              <Typography variant="h5" sx={{ mb: 3, opacity: 0.9 }}>
                Your tenancy would appear to be compliant subject to being verified
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {scoreState.totalScore}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Total Score
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'center' }}>
                  <StarRating rating={scoreState.starRating} size="large" />
                  <Typography variant="body1" sx={{ mt: 1, opacity: 0.9 }}>
                    {scoreState.complianceLevel?.name || 'Compliance Level'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Chip
                icon={<Home />}
                label={`Level ${scoreState.currentLevel} Completed`}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
              <Chip
                icon={<CheckCircle />}
                label={`${scoreState.answers.length} Questions Answered`}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
              {!scoreState.isVerified && (
                <Chip
                  icon={<VerifiedUser />}
                  label="Unverified Score"
                  sx={{
                    bgcolor: 'rgba(255,193,7,0.3)',
                    color: 'white',
                    '& .MuiChip-icon': { color: 'white' }
                  }}
                />
              )}
            </Box>
          </CardContent>

          {/* Background decoration */}
          <Box
            sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.1)',
              zIndex: 0
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -30,
              left: -30,
              width: 150,
              height: 150,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.1)',
              zIndex: 0
            }}
          />
        </Card>
      </motion.div>

      {/* Action Buttons */}
      <motion.div variants={itemVariants}>
        <Box sx={{ display: 'flex', gap: 2, mb: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/signup')}
            startIcon={<VerifiedUser />}
            size="large"
          >
            Get Verified
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/assessment')}
            startIcon={<Refresh />}
            size="large"
          >
            Retake Assessment
          </Button>
          <Button
            variant="outlined"
            startIcon={<Share />}
            size="large"
          >
            Share Results
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            size="large"
          >
            Download Report
          </Button>
        </Box>
      </motion.div>

      <Grid container spacing={4}>
        {/* Score Breakdown */}
        <Grid item xs={12} md={6}>
          <motion.div variants={itemVariants}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 3 }}>
                  Score Breakdown
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
                    Compliance Level: <strong>{scoreState.complianceLevel?.name}</strong>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {scoreState.complianceLevel?.description}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Property Owner: {userState.contact?.firstName} {userState.contact?.lastName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Property Address: {userState.property?.address.street}, {userState.property?.address.city}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Assessment Date: {new Date().toLocaleDateString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Improvement Suggestions */}
        <Grid item xs={12} md={6}>
          <motion.div variants={itemVariants}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 3 }}>
                  Improvement Suggestions
                </Typography>

                <List dense>
                  {improvements.slice(0, 5).map((improvement, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <TrendingUp color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={improvement}
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem' } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Next Level Requirements */}
        {scoreState.currentLevel < 5 && (
          <Grid item xs={12}>
            <motion.div variants={itemVariants}>
              <Card sx={{ border: '2px solid', borderColor: 'primary.main' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Star sx={{ mr: 2, color: 'primary.main' }} />
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                      Unlock Level {scoreState.currentLevel + 1}
                    </Typography>
                  </Box>

                  <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                    Complete these requirements to unlock the next compliance level and earn more stars:
                  </Typography>

                  <Grid container spacing={2}>
                    {nextLevelRequirements.map((requirement, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                          <CheckCircle sx={{ mr: 2, color: 'success.main' }} />
                          <Typography variant="body2">
                            {requirement}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => navigate('/assessment')}
                      startIcon={<Star />}
                    >
                      Continue to Level {scoreState.currentLevel + 1}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        )}

        {/* Achievements */}
        <Grid item xs={12}>
          <motion.div variants={itemVariants}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 3, textAlign: 'center' }}>
              Achievements Unlocked
            </Typography>

            <Grid container spacing={2}>
              {unlockedAchievements.map((achievement) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={achievement.id}>
                  <AchievementBadge
                    achievement={achievement}
                    size="small"
                    showProgress={false}
                  />
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Grid>
      </Grid>

      {/* Call to Action */}
      <motion.div variants={itemVariants}>
        <Box sx={{ textAlign: 'center', mt: 6, py: 4 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
            Ready to Make Your Score Official?
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            Verify your compliance score through our official verification process
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/verification')}
            sx={{
              px: 4,
              py: 2,
              background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
              boxShadow: '0 8px 32px rgba(25, 118, 210, 0.3)'
            }}
          >
            Start Verification Process
          </Button>
        </Box>
      </motion.div>
    </motion.div>
  );
};

export default ResultsPage;